import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getAvatarHintStep,
    getScreenShareHintStep,
    getThemeHintStep,
    OnboardingHintStep,
    persistAvatarHintStep,
    persistScreenShareHintStep,
    persistThemeHintStep,
} from '@/utils/avatarHint';

export type NavbarOnboardingHint = 'avatar' | 'theme' | 'screenShare';

/** Задержка перед первой подсказкой: шапка успевает занять финальную геометрию. */
export const HINT_REVEAL_DELAY_MS = 1_500;
/** Между шагами оставляем короткую паузу, чтобы два popover не сменяли друг друга внахлёст. */
export const HINT_STEP_REVEAL_DELAY_MS = 350;
/** Подсказка — не диалог: если её не закрыли, она сама исчезает и больше не возвращается. */
export const HINT_AUTO_DISMISS_MS = 8_000;

type HintSteps = Record<NavbarOnboardingHint, OnboardingHintStep>;

const ALL_DONE: HintSteps = { avatar: 'done', theme: 'done', screenShare: 'done' };

const PERSIST: Record<NavbarOnboardingHint, (userId: number | string, step: 1 | 'done') => void> = {
    avatar: persistAvatarHintStep,
    theme: persistThemeHintStep,
    screenShare: persistScreenShareHintStep,
};

interface UseNavbarOnboardingParams {
    userId?: number | string;
    hasImage: boolean;
    /** Меню профиля открыто */
    menuOpen: boolean;
    /** Меню «Помощь» открыто: в нём второй шаг подсказки про показ экрана */
    helpMenuOpen?: boolean;
    /** В меню «Помощь» есть «Показать экран поддержке» (во вкладке «от имени» — нет): без него подсказки нет */
    screenShareAvailable?: boolean;
    /**
     * Подсказки ждут: идёт показ экрана поддержке — они лежали бы поверх его плашки и закрывали «Продолжить показ»
     * (IMPLEMENTATION.md §35)
     */
    suppressed?: boolean;
}

/**
 * Последовательный онбординг шапки. В каждый момент активна только одна подсказка: сначала аватар (если его нет),
 * затем новый переключатель темы, затем показ экрана поддержке. Каждая — из шага на кнопке (профиля или «Помощь») и
 * шага внутри открытого меню этой кнопки.
 */
export function useNavbarOnboarding({
    userId,
    hasImage,
    menuOpen,
    helpMenuOpen = false,
    screenShareAvailable = false,
    suppressed = false,
}: UseNavbarOnboardingParams) {
    const [steps, setSteps] = useState<HintSteps>(ALL_DONE);
    const [readyFor, setReadyFor] = useState<string | null>(null);

    useEffect(() => {
        if (!userId) {
            setSteps(ALL_DONE);
            return;
        }

        const storedAvatarStep = getAvatarHintStep(userId);
        const storedThemeStep = getThemeHintStep(userId);
        const storedScreenShareStep = getScreenShareHintStep(userId);

        // Шаг 1 имеет смысл только пока меню открыто. После перезагрузки начинаем его
        // снова с кнопки, иначе очередь может навсегда остаться невидимой.
        setSteps({
            avatar: hasImage ? 'done' : storedAvatarStep === 1 ? 0 : storedAvatarStep,
            theme: storedThemeStep === 1 ? 0 : storedThemeStep,
            screenShare: storedScreenShareStep === 1 ? 0 : storedScreenShareStep,
        });
    }, [hasImage, userId]);

    // Очередь фиксирована намеренно: так подсказки на одной кнопке и внутри одного меню
    // никогда не могут открыться одновременно.
    const activeHint = useMemo<NavbarOnboardingHint | null>(() => {
        if (!hasImage && steps.avatar !== 'done') return 'avatar';
        if (steps.theme !== 'done') return 'theme';
        if (screenShareAvailable && steps.screenShare !== 'done') return 'screenShare';
        return null;
    }, [hasImage, screenShareAvailable, steps.avatar, steps.screenShare, steps.theme]);
    const activeStep = activeHint ? steps[activeHint] : 'done';
    // Меню, внутри которого второй шаг активной подсказки
    const ownMenuOpen = activeHint === 'screenShare' ? helpMenuOpen : menuOpen;
    const anyMenuOpen = menuOpen || helpMenuOpen;

    const setHintStep = useCallback(
        (hint: NavbarOnboardingHint, step: 1 | 'done') => {
            setSteps((current) => (current[hint] === step ? current : { ...current, [hint]: step }));

            if (userId) PERSIST[hint](userId, step);
        },
        [userId],
    );

    const dismissHint = useCallback(
        (hint: NavbarOnboardingHint) => {
            setHintStep(hint, 'done');
        },
        [setHintStep],
    );

    // Открытие меню переводит текущую подсказку с общей кнопки на конкретный контрол. Пока подсказки ждут (показ
    // экрана), шаг не двигается: иначе меню, открытое во время показа, пометило бы их пройденными, не показав.
    useEffect(() => {
        if (!suppressed && ownMenuOpen && activeHint && activeStep === 0) {
            setHintStep(activeHint, 1);
        }
    }, [activeHint, activeStep, ownMenuOpen, setHintStep, suppressed]);

    // Шаг на кнопке — пока не открыто ни одно меню шапки: он лежал бы поверх меню соседней кнопки.
    const visibleHintKey =
        !suppressed && activeHint && ((activeStep === 0 && !anyMenuOpen) || (activeStep === 1 && ownMenuOpen))
            ? `${activeHint}:${activeStep}`
            : null;

    useEffect(() => {
        if (!visibleHintKey) {
            setReadyFor(null);
            return;
        }

        setReadyFor(null);
        const timeout = window.setTimeout(
            () => setReadyFor(visibleHintKey),
            activeStep === 0 ? HINT_REVEAL_DELAY_MS : HINT_STEP_REVEAL_DELAY_MS,
        );

        return () => window.clearTimeout(timeout);
    }, [activeStep, visibleHintKey]);

    const hintOpen = !!visibleHintKey && readyFor === visibleHintKey;
    // Карточка показа экрана в меню — с кнопками и фактами, её читают: сама не уходит, её закрывает закрытие меню.
    const autoDismiss = !(activeHint === 'screenShare' && activeStep === 1);

    useEffect(() => {
        if (!hintOpen || !activeHint || !autoDismiss) return;

        const timeout = window.setTimeout(() => dismissHint(activeHint), HINT_AUTO_DISMISS_MS);
        return () => window.clearTimeout(timeout);
    }, [activeHint, autoDismiss, dismissHint, hintOpen]);

    return { activeHint, activeStep, hintOpen, dismissHint };
}
